/**
 * Typed adapter over @capacit-com/lara-law-shared-ui WorkspaceSelectionDialog.
 *
 * Same casting boundary as workspace-explorer.tsx — web-app's enum-based
 * WorkspaceDocument types don't structurally satisfy shared-ui's string-literal
 * types at compile time, so we cast once here and re-export web-app types.
 */
import type { ReactNode } from "react";
import {
  WorkspaceSelectionDialog as SharedWorkspaceSelectionDialog,
  type WorkspaceExplorerLabels,
} from "@capacit-com/lara-law-shared-ui";
import type {
  Workspace as SharedWorkspace,
  WorkspaceDocument as SharedWorkspaceDocument,
  WorkspaceSelectableItem as SharedSelectableItem,
  WorkspaceSelectionEntry as SharedSelectionEntry,
} from "@capacit-com/lara-law-shared-ui";
import type Workspace from "@/models/workspace/Workspace";
import type { ExplorerSelectionEntry } from "@/lib/buildConversationWorkspaceDocuments";
import type {
  WorkspaceDocument,
  WorkspaceFolder,
} from "@/models/workspace/Workspace";

const DANISH_LABELS: WorkspaceExplorerLabels = {
  emptyFolder: "(Tom mappe)",
  folderWithoutFiles: "(Mappe uden filer)",
  noSelectableFiles: "(Ingen klargjorte eller gældende filer)",
  noSelectableFilesTooltip: "Ingen klargjorte eller gældende filer i mappen",
  noSelectableWorkspaceTooltip:
    "Ingen klargjorte eller gældende filer i workspace",
  notIndexed: "Kildeklargjort",
  notCurrent: "Ikke-gældende",
  expandAllSubfolders: "Åbn alle undermapper",
  collapseAllSubfolders: "Luk alle undermapper",
  noSelectableFilesWorkspace: "(Ingen klargjorte eller gældende filer)",
  searchPlaceholder: "Søg i dokumenter...",
  noSearchResults: "Ingen dokumenter fundet",
  selectAllWorkspaces: "Vælg alle workspaces",
};

export type WorkspaceSelectionDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  workspaces: Workspace[];
  isLoading?: boolean;
  emptyMessage: string;
  selectedItems: Array<WorkspaceDocument | WorkspaceFolder>;
  onSelectItem: (
    item: WorkspaceDocument | WorkspaceFolder,
    workspaceId: string
  ) => void;
  onDeselectWorkspace: (workspaceId: string) => void;
  /** Enables the explorer's "select all workspaces" row. Omit to hide it. */
  onSelectAllWorkspaces?: (entries: ExplorerSelectionEntry[]) => void;
  /**
   * Which documents may be selected at all. Defaults to the chat rule (indexed
   * and not historic); a picker meant to reach every document passes
   * `() => true`. Give the same rule to the selection reducer.
   */
  isItemSelectable?: (doc: WorkspaceDocument) => boolean;
  onConfirm: () => void;
  confirmLabel: string;
  /** Cancel button label. Defaults to "Annuller". Pass null to hide. */
  cancelLabel?: string | null;
  warningBanner?: ReactNode;
  explorerDisabled?: boolean;
  showSearch?: boolean;
  labels?: WorkspaceExplorerLabels;
  trigger?: ReactNode;
  contentClassName?: string;
  footer?: ReactNode;
};

export function WorkspaceSelectionDialog({
  workspaces,
  selectedItems,
  onSelectItem,
  onDeselectWorkspace,
  onSelectAllWorkspaces,
  isItemSelectable,
  labels,
  cancelLabel = "Annuller",
  ...rest
}: WorkspaceSelectionDialogProps) {
  return (
    <SharedWorkspaceSelectionDialog
      {...rest}
      cancelLabel={cancelLabel}
      workspaces={workspaces as unknown as SharedWorkspace[]}
      selectedItems={selectedItems as unknown as SharedSelectableItem[]}
      onSelectItem={
        onSelectItem as unknown as (
          item: SharedSelectableItem,
          workspaceId: string
        ) => void
      }
      onDeselectWorkspace={onDeselectWorkspace}
      onSelectAllWorkspaces={
        onSelectAllWorkspaces as unknown as
          ((entries: SharedSelectionEntry[]) => void) | undefined
      }
      isItemSelectable={
        isItemSelectable as unknown as
          ((doc: SharedWorkspaceDocument) => boolean) | undefined
      }
      labels={{ ...DANISH_LABELS, ...labels }}
    />
  );
}
